import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import type { Purchase } from "../types";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import stellar from "@/services/stellar";

type ConfirmPurchaseDialogProps = {
	purchase: Purchase | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirmed: () => void;
};

export const ConfirmPurchaseDialog: React.FC<ConfirmPurchaseDialogProps> = ({
	purchase,
	open,
	onOpenChange,
	onConfirmed,
}) => {
	const [isConfirmOpen, setIsConfirmOpen] = useState(open);
	const [isStoreBoxOpen, setIsStoreBoxOpen] = useState(false);
	const [storeBoxValue, setStoreBoxValue] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		setIsConfirmOpen(open);

		if (open) {
			setIsStoreBoxOpen(false);
			setStoreBoxValue("");
		}
	}, [open]);

	const resetState = useCallback(() => {
		setIsConfirmOpen(false);
		setIsStoreBoxOpen(false);
		setStoreBoxValue("");
	}, []);

	const closeAll = useCallback(() => {
		resetState();
		onOpenChange(false);
	}, [onOpenChange, resetState]);

	const confirmPurchase = useCallback(
		async (storeBox: string) => {
			if (!purchase) {
				return;
			}

			setIsSubmitting(true);

			try {
				await stellar.action("confirmPurchase", {
					id: purchase.id,
					storeBox,
				});
				toast.success("Compra efetivada.");

				if (storeBox.trim()) {
					toast.success(`A peça deve ser colocada na caixa ${storeBox}.`);
				}

				onConfirmed();
			} catch {
				toast.error("Não foi possível efetivar a compra. Tente novamente.");
			} finally {
				setIsSubmitting(false);
				closeAll();
			}
		},
		[purchase, onConfirmed, closeAll],
	);

	const handleConfirmContinue = useCallback(() => {
		if (!purchase) {
			return;
		}

		const materialStoreBox = purchase.material.storeBox?.trim() ?? "";

		if (materialStoreBox) {
			void confirmPurchase(materialStoreBox);
			return;
		}

		setIsConfirmOpen(false);
		setIsStoreBoxOpen(true);
		setStoreBoxValue("");
		onOpenChange(false);
	}, [purchase, confirmPurchase, onOpenChange]);

	const handleAlertOpenChange = useCallback(
		(nextOpen: boolean) => {
			setIsConfirmOpen(nextOpen);

			if (!nextOpen) {
				closeAll();
			} else {
				onOpenChange(true);
			}
		},
		[closeAll, onOpenChange],
	);

	const handleStoreBoxOpenChange = useCallback(
		(nextOpen: boolean) => {
			setIsStoreBoxOpen(nextOpen);

			if (!nextOpen) {
				closeAll();
			}
		},
		[closeAll],
	);

	const handleStoreBoxSubmit = useCallback(() => {
		const trimmed = storeBoxValue.trim();

		if (!trimmed) {
			return;
		}

		void confirmPurchase(trimmed);
	}, [storeBoxValue, confirmPurchase]);

	return (
		<>
			<AlertDialog open={isConfirmOpen} onOpenChange={handleAlertOpenChange}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Efetivar compra</AlertDialogTitle>
						<AlertDialogDescription>
							Tem a certeza que pretende efetivar a compra?
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isSubmitting}>
							Cancelar
						</AlertDialogCancel>
						<AlertDialogAction
							className="bg-emerald-600 text-white hover:bg-emerald-500"
							onClick={handleConfirmContinue}
							disabled={!purchase || isSubmitting}
							aria-busy={isSubmitting ? "true" : undefined}
						>
							{isSubmitting ? "A efetivar..." : "Continuar"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<Dialog open={isStoreBoxOpen} onOpenChange={handleStoreBoxOpenChange}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Caixa do material</DialogTitle>
						<DialogDescription>
							Indique a caixa onde o material será colocado.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-2">
						<Label htmlFor="storeBox">Caixa</Label>
						<Input
							id="storeBox"
							value={storeBoxValue}
							onChange={(event) => setStoreBoxValue(event.target.value)}
							disabled={isSubmitting}
						/>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							type="button"
							onClick={closeAll}
							disabled={isSubmitting}
						>
							Cancelar
						</Button>
						<Button
							type="button"
							onClick={handleStoreBoxSubmit}
							disabled={isSubmitting || storeBoxValue.trim().length === 0}
						>
							{isSubmitting ? "A efetivar..." : "Continuar"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};
